/**
 * Supplies the header bell (`@steelyard/theme`'s copied `notification-dropdown.tsx`) with Identity's
 * back-office inbox, the way `pages/root.tsx` supplies `SessionProvider`. No markup of its own.
 *
 * Reads the newest page on mount, every minute while the tab is visible, and when the window regains
 * focus. A mark-read waits for the server and then re-reads, so the badge is always the API's own
 * `unreadCount`, never a number decremented here.
 *
 * Browser push (`lib/push.ts`): on mount, re-registers this browser's push token when permission was
 * already granted, and a push arriving while the tab is open shows as a toast and re-reads the inbox
 * at once, so the bell does not wait for the next poll.
 */
import { useCallback, useEffect, useState, type PropsWithChildren } from "react";
import toast from "react-hot-toast";
import { NotificationsProvider, type NotificationItem } from "@steelyard/theme";
import { ApiError } from "../lib/api";
import { enablePush, onForegroundPush } from "../lib/push";
import { listNotifications, markAllNotificationsRead, markNotificationRead } from "../lib/services/notifications";

// ponytail: polling; server-sent events if a minute's delay ever matters.
const POLL_MS = 60_000;

const describe = (error: unknown) =>
  error instanceof ApiError
    ? (error.detail ?? error.title ?? `Notifications could not be read (HTTP ${error.status}).`)
    : "Notifications could not be read.";

export function NotificationsHost({ children }: PropsWithChildren) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const page = await listNotifications({ page: 1, pageSize: 10 });
      setItems(page.items);
      setUnreadCount(page.unreadCount);
      setError(null);
    } catch (caught) {
      setError(describe(caught));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onFocus = () => void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  useEffect(() => {
    // Best effort: push is a courtesy beside the inbox, never a reason to show an error.
    void enablePush(false).catch(() => {});
    const unsubscribe = onForegroundPush(({ title, body }) => {
      toast(`${title}${body ? ` — ${body}` : ""}`);
      void refresh();
    });
    return () => void unsubscribe.then((stop) => stop());
  }, [refresh]);

  const after = useCallback(
    async (write: Promise<void>) => {
      try {
        await write;
      } catch (caught) {
        setError(describe(caught));
        return;
      }
      await refresh();
    },
    [refresh],
  );

  return (
    <NotificationsProvider
      value={{
        items,
        unreadCount,
        error,
        markRead: (id) => after(markNotificationRead(id)),
        markAllRead: () => after(markAllNotificationsRead()),
        viewAllHref: "/notifications",
      }}
    >
      {children}
    </NotificationsProvider>
  );
}
